/**
 * Batches activity events so the network sees one request per flush instead
 * of one per click (dev-best-practices §7: the network is a rare resource).
 *
 * Failure policy: a batch that fails to send is DROPPED with a single
 * console.warn. No retry, no re-queue — analytics must never compete with the
 * app for the network or accumulate unbounded state in a tab that has lost
 * connectivity.
 */
import type { UserActivityEvent } from './user-activity-event';

export interface UserActivityTransport {
    /** Normal path; may reject. */
    Send(events: ReadonlyArray<UserActivityEvent>): Promise<void>;
    /** Unload path; must not depend on the page surviving (keepalive fetch). */
    SendOnUnload(events: ReadonlyArray<UserActivityEvent>): void;
}

export const USER_ACTIVITY_FLUSH_MAX = 20;
export const USER_ACTIVITY_FLUSH_DELAY_MS = 5000;

export class UserActivityQueue {
    private pending: UserActivityEvent[] = [];
    private timer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private readonly transport: UserActivityTransport,
        private readonly maxBatch: number = USER_ACTIVITY_FLUSH_MAX,
        private readonly delayMs: number = USER_ACTIVITY_FLUSH_DELAY_MS
    ) {}

    get PendingCount(): number {
        return this.pending.length;
    }

    Record(event: UserActivityEvent): void {
        this.pending.push(event);
        if (this.pending.length >= this.maxBatch) {
            void this.Flush();
        } else if (this.timer === null) {
            this.timer = setTimeout(() => void this.Flush(), this.delayMs);
        }
    }

    async Flush(): Promise<void> {
        this.clearTimer();
        const batch = this.takeAll();
        if (batch.length === 0) return;
        try {
            await this.transport.Send(batch);
        } catch (e) {
            console.warn(`[user-activity] dropped ${batch.length} event(s): send failed`, e);
        }
    }

    /**
     * An unload handler must never throw: a synchronous exception here would
     * propagate uncaught through the pagehide/visibilitychange listener that
     * calls it, so any transport failure is dropped and warned exactly like
     * Flush's failure policy.
     */
    FlushOnUnload(): void {
        this.clearTimer();
        const batch = this.takeAll();
        if (batch.length === 0) return;
        try {
            this.transport.SendOnUnload(batch);
        } catch (e) {
            console.warn(`[user-activity] dropped ${batch.length} event(s): unload send failed`, e);
        }
    }

    private takeAll(): UserActivityEvent[] {
        return this.pending.splice(0, this.pending.length);
    }

    private clearTimer(): void {
        if (this.timer !== null) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }
}
